import { useState, useEffect, useContext } from 'react'
import { useSelector } from 'react-redux'
import { ModalContext } from '../ModalContext'
import { WhichWeightAPI } from 'WhichWeightAPI'
import moment from 'moment'

import BrandColorButton from 'components/Buttons/BrandColorButton'

export default function EditAthleteMembershipModal() {

    const organizationMembershipNotificationStrings = {
        'free-trial': 'Free Trial Start',
        'activation': 'Membership Start',
        'deactivation': 'Membership End',
        'deactivation-with-no-proration': 'Membership End',
        'pause': 'Pause Start'
    }

    const { modalData } = useContext(ModalContext)

    const organizationId = useSelector((state) => state.user.organizationId)

    const [organizationMembershipNotifications, setOrganizationMembershipNotifications] = useState(null)
    const [organizationSeatPriceOverrideEnabled, setOrganizationSeatPriceOverrideEnabled] = useState(null)
    // const [organizationSeatPriceOverrideValue, setOrganizationSeatPriceOverrideValue] = useState(null)
    const [saveLoading, setSaveLoading] = useState(false)

    const GetUserOrganizationMembershipInfo = async () => {
        const jsonBody = {
            userId: modalData.athlete.id,
            organizationId: organizationId
        }

        // console.log(jsonBody)

        const result = await WhichWeightAPI('BILLING', 'GET', '/user_organization_membership_info', jsonBody)

        // console.log(result)

        if (result?.data?.status === 'failure') {
            console.error(result)
            return
        }

        const membershipInfo = result.data.data

        setOrganizationMembershipNotifications(membershipInfo.organizationMembershipNotifications)
        setOrganizationSeatPriceOverrideEnabled(membershipInfo.organizationSeatPriceOverride !== null)
        // setOrganizationSeatPriceOverrideValue(membershipInfo.organizationSeatPriceOverride !== null ? String(membershipInfo.organizationSeatPriceOverride) : '')
    }

    const GetLocalDateInputValue = (notificationTimestamp) => {
        return moment.utc(notificationTimestamp).local().format('YYYY-MM-DD')
    }

    const UpdateNotificationDate = (notificationId, dateInputValue) => {
        setOrganizationMembershipNotifications((currentNotifications) => {
            return currentNotifications.map((notification) => {
                if (notification.id !== notificationId){ 
                    return notification
                }

                /*
                    Timestamp conversion logic:
                    1. Take local time and slap it onto selected input data value.
                    2. Convert back to UTC for storage
                */

                // 1) Parse the existing backend timestamp as UTC.
                // This is the source of truth for persisted data.
                const originalUtcTimestamp = moment.utc(notification.notificationTimestamp)
                // console.log('Original UTC timestamp: ', originalUtcTimestamp.format('YYYY-MM-DD HH:mm:ss.SS'))

                // 2) Convert that UTC timestamp into local time.
                // We do this because the date picker is local-time based.
                const originalLocalTimestamp = originalUtcTimestamp.clone().local()
                // console.log('Original local timestamp: ', originalLocalTimestamp.format('YYYY-MM-DD HH:mm:ss.SS'))

                // 3) Parse the newly selected local calendar date from <input type="date">.
                // This value has only year/month/day and no meaningful time-of-day.
                const selectedLocalDate = moment(dateInputValue, 'YYYY-MM-DD')
                // console.log('Selected local date: ', selectedLocalDate.format('YYYY-MM-DD HH:mm:ss.SS'))

                // 4) Build a new local timestamp by replacing only the date portion.
                // Keep the original local hour/min/sec/ms intact.
                const updatedLocalTimestamp = originalLocalTimestamp
                    .clone()
                    .year(selectedLocalDate.year())
                    .month(selectedLocalDate.month())
                    .date(selectedLocalDate.date())
                // console.log('Updated local timestamp: ', updatedLocalTimestamp.format('YYYY-MM-DD HH:mm:ss.SS'))

                // 5) Convert back to UTC so state + save payload stay UTC.
                const updatedUtcTimestamp = updatedLocalTimestamp.clone().utc()
                // console.log('Updated UTC timestamp: ', updatedUtcTimestamp.format('YYYY-MM-DD HH:mm:ss.SS'))

                // console.log('--------------------------------')

                return {
                    ...notification,
                    // 6) Store as UTC-formatted string for backend consistency.
                    notificationTimestamp: updatedUtcTimestamp.format('YYYY-MM-DD HH:mm:ss.SS'),
                }
            })
        })
    }

    const SaveOrganizationMembershipInfo = async () => {
        setSaveLoading(true)

        const jsonBody = {
            userId: modalData.athlete.id,
            organizationMembershipNotifications: JSON.stringify(organizationMembershipNotifications),
            organizationSeatPriceOverride: organizationSeatPriceOverrideEnabled ? 0.00 : null
            // organizationSeatPriceOverride: organizationSeatPriceOverrideEnabled ? organizationSeatPriceOverrideValue : null
        }

        // console.log(jsonBody)

        const result = await WhichWeightAPI('BILLING', 'POST', '/user_organization_membership_info', jsonBody)

        // console.log(result)

        setSaveLoading(false)

        if (result?.data?.status === 'failure') {
            console.error(result)
            return
        }
        
        window.location.reload()
    }

    useEffect(() => { GetUserOrganizationMembershipInfo() }, [])

    if (
        organizationMembershipNotifications === null
        || organizationSeatPriceOverrideEnabled === null
        // || organizationSeatPriceOverrideValue === null
    ) {
        return (
            <>
                <div className="text-center text-lg font-semibold leading-6 text-gray-900">
                    {modalData.athlete.firstName} {modalData.athlete.lastName} Membership Info
                </div>

                <div className="animate-pulse space-y-4">
                    <div className="h-20 bg-gray-200 rounded w-full"></div>
                    <div className="h-20 bg-gray-200 rounded w-full"></div>
                    <div className="h-20 bg-gray-200 rounded w-full"></div>
                </div>
            </>
        )
    }

    return (
        <>
            <div className="text-center text-lg font-semibold leading-6 text-gray-900">
                {modalData.athlete.firstName} {modalData.athlete.lastName} Membership Info
            </div>

            <div className='space-y-4'>
                <div className='bg-gray-100 p-4 rounded-md max-h-72 overflow-y-auto'>
                    {organizationMembershipNotifications.length === 0 && (
                        <div className='text-sm text-gray-500'>No membership notifications found.</div>
                    )}

                    {organizationMembershipNotifications.length > 0 && (
                        <div className='relative pl-6 space-y-4'>
                            <div className='absolute left-[7px] top-2 bottom-2 w-px bg-gray-300' />

                            {organizationMembershipNotifications.map((notification, index) => (
                                <div 
                                    key={notification.id} 
                                    className='relative flex items-center gap-x-2'
                                >
                                    <div className='absolute left-[-22px] top-3 h-3 w-3 rounded-full bg-orange-400 border-2 border-white' />

                                    <p className='text-sm'>({index + 1})</p>

                                    <div className='text-left text-sm font-semibold flex-grow'>
                                        {organizationMembershipNotificationStrings[notification.notificationType] ? organizationMembershipNotificationStrings[notification.notificationType] : notification.notificationType}
                                    </div>

                                    <div className='border border-gray-300 rounded-md bg-white px-2 py-1'>
                                        <input
                                            type='date'
                                            value={GetLocalDateInputValue(notification.notificationTimestamp)}
                                            onChange={(event) => UpdateNotificationDate(notification.id, event.target.value)}
                                            className='text-xs text-gray-800 bg-transparent outline-none'
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div 
                    className='flex items-center gap-x-2 bg-gray-100 p-4 rounded-md cursor-pointer'
                    onClick={() => setOrganizationSeatPriceOverrideEnabled((current) => !current)}
                >
                    <div className='text-left text-sm flex-1'>Free Seat</div>

                    <button
                        className={`
                            relative inline-flex h-6 w-11 items-center rounded-full transition-colors
                            ${organizationSeatPriceOverrideEnabled ? 'bg-orange-400' : 'bg-gray-300'}
                        `}
                        
                        type='button'
                    >
                        <span
                            className={`
                                inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                                ${organizationSeatPriceOverrideEnabled ? 'translate-x-6' : 'translate-x-1'}
                            `}
                        />
                    </button>

                    {/* <div className='flex-1' /> */}

                    {/* <div className='text-sm text-gray-600'>$</div> */}

                    {/* <input
                        type='number'
                        step='0.01'
                        value={organizationSeatPriceOverrideValue}
                        onChange={(event) => setOrganizationSeatPriceOverrideValue(event.target.value)}
                        disabled={!organizationSeatPriceOverrideEnabled}
                        className='w-20 text-sm rounded-md py-2 px-2 text-gray-900 ring-1 ring-inset ring-gray-300 disabled:bg-gray-100 disabled:text-gray-400'
                        placeholder='0.00'
                    /> */}
                </div>
            </div>

            <BrandColorButton
                text='Save'
                action={() => SaveOrganizationMembershipInfo()}
                isLoading={saveLoading}
            />
        </>
    )
}
